import { BusinessObjectBase } from './base';
import { resourceCategoryItemSchema } from '@schemas/business-objects/resource-category-item';
import type { Resource } from './resource';
import type { ResourceCategory } from './resource-category';
import type { Id } from '../base-types';
import type { TextBox } from './text-box';
import type { User } from './user';

export interface ResourceCategoryItemData {
  readonly id: Id;
  readonly sequence?: number;
  readonly resourceCategoryId: Id;
  readonly resourceCategory?: ResourceCategory;
  readonly resourceId: Id;
  readonly resource?: Resource;
  readonly locked?: boolean;
}

export interface ResourceCategoryItem extends Readonly<ResourceCategoryItemData> {}
export class ResourceCategoryItem extends BusinessObjectBase {
  static readonly entityName = 'ResourceCategoryItem' as const;
  static readonly plural = 'resource-category-items';
  static schema = resourceCategoryItemSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ResourceCategoryItem[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ResourceCategoryItem(data));
  }

  static async findOne(filter?: any): Promise<ResourceCategoryItem | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ResourceCategoryItem(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ResourceCategoryItem> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ResourceCategoryItem(data);
  }

  static async create(
    data: Omit<ResourceCategoryItemData, 'id'>,
  ): Promise<ResourceCategoryItem> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ResourceCategoryItem(result);
  }

  static async createMany(
    data: Omit<ResourceCategoryItemData, 'id'>[],
  ): Promise<ResourceCategoryItem[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ResourceCategoryItem(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ResourceCategoryItemData>,
  ): Promise<ResourceCategoryItem> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ResourceCategoryItem(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  async dataForCopy(): Promise<any> {
    const url = `${BusinessObjectBase.baseUrl}/${ResourceCategoryItem.plural}/${(this as any).id}/data-for-copy`;
    return BusinessObjectBase.post<any>(url, {});
  }
}
